import React from 'react';
import { useTranslation } from 'react-i18next';

import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined';
import {
	Alert,
	Autocomplete,
	Box,
	CardActionArea,
	CircularProgress,
	Paper,
	TextField,
	Typography,
} from '@mui/material';

import { BookingFlow } from '../../hooks/useBookingFlow';
import { cardSx, twoColumnsSx } from '../../styled';
import ErrorNotice from '../ErrorNotice';

interface ChoiceFieldProps {
	label: string;
	options: { id: number; name: string }[];
	value: number | null;
	onChange: (id: number | null) => void;
	error?: string;
	disabled?: boolean;
	loading?: boolean;
}

function ChoiceField({
	label,
	options,
	value,
	onChange,
	error,
	disabled,
	loading,
}: ChoiceFieldProps) {
	const { t } = useTranslation();
	return (
		<Autocomplete
			sx={{ minWidth: 0, width: '100%' }}
			options={options}
			value={options.find((option) => option.id === value) || null}
			getOptionLabel={(option) => option.name}
			isOptionEqualToValue={(option, selected) => option.id === selected.id}
			onChange={(_, option) => onChange(option?.id ?? null)}
			disabled={disabled}
			loading={loading}
			noOptionsText={t('academyReworkings.noOptions')}
			renderInput={(params) => (
				<TextField {...params} required label={label} error={!!error} helperText={error} />
			)}
		/>
	);
}

export default function Start({ flow }: { flow: BookingFlow }) {
	const { t } = useTranslation();
	return (
		<Box display="flex" flexDirection="column" gap="24px">
			<Box>
				<Typography variant="h3">
					{t('academyReworkings.welcome', { name: flow.studentName })}
				</Typography>
				<Typography color="text.secondary" sx={{ mt: 1 }}>
					{t('academyReworkings.startDescription')}
				</Typography>
			</Box>
			{!flow.studentId && <Alert severity="info">{t('academyReworkings.studentRequired')}</Alert>}
			<Box
				sx={(theme) => ({
					...twoColumnsSx(theme),
					[theme.breakpoints.up('md')]: { gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' },
				})}
			>
				<Box sx={{ minWidth: 0 }}>
					<ChoiceField
						label={t('academyReworkings.location')}
						options={flow.locations}
						value={flow.locationId}
						onChange={flow.changeLocation}
						disabled={flow.busy}
						error={
							flow.attempted && !flow.locationId ? t('academyReworkings.selectLocation') : undefined
						}
					/>
				</Box>
				<Box sx={{ minWidth: 0 }}>
					<ChoiceField
						label={t('academyReworkings.group')}
						options={flow.groups.filter((group) => group.locationId === flow.locationId)}
						value={flow.groupId}
						onChange={flow.changeGroup}
						disabled={flow.busy || !flow.locationId}
						error={flow.attempted && !flow.groupId ? t('academyReworkings.selectGroup') : undefined}
					/>
				</Box>
				<Box sx={{ minWidth: 0 }}>
					<ChoiceField
						label={t('academyReworkings.program')}
						options={flow.programs.map((program) => ({
							id: program.program_id,
							name: program.program_name,
						}))}
						value={flow.programId}
						onChange={flow.setProgramId}
						disabled={flow.busy || !flow.groupId}
						loading={flow.programsLoading}
						error={
							flow.attempted &&
							!flow.programs.some((program) => program.program_id === flow.programId)
								? t('academyReworkings.selectProgram')
								: undefined
						}
					/>
				</Box>
			</Box>
			{flow.catalogueError && (
				<ErrorNotice message={flow.catalogueError} onRetry={flow.retryCatalogue} />
			)}
			<Box sx={twoColumnsSx}>
				<Box sx={{ minWidth: 0 }}>
					<Paper sx={{ borderRadius: '20px', overflow: 'hidden', height: '100%' }}>
						<CardActionArea
							onClick={flow.startBooking}
							disabled={flow.busy || flow.programsLoading || !flow.studentId}
							sx={(theme) => ({ ...cardSx(theme), height: '100%' })}
						>
							<AddCircleOutlineIcon color="primary" sx={{ fontSize: '44px', mb: 2 }} />
							<Typography variant="h3">{t('academyReworkings.book')}</Typography>
							<Typography color="text.secondary" sx={{ mt: 1 }}>
								{t('academyReworkings.bookDescription')}
							</Typography>
							{flow.busy && <CircularProgress size={20} sx={{ mt: 1 }} />}
						</CardActionArea>
					</Paper>
				</Box>
				<Box sx={{ minWidth: 0 }}>
					<Paper sx={{ borderRadius: '20px', overflow: 'hidden', height: '100%' }}>
						<CardActionArea
							onClick={() => flow.setStep('bookings')}
							disabled={flow.busy}
							sx={(theme) => ({ ...cardSx(theme), height: '100%' })}
						>
							<EventAvailableOutlinedIcon color="primary" sx={{ fontSize: '44px', mb: 2 }} />
							<Typography variant="h3">{t('academyReworkings.myBookings')}</Typography>
							<Typography color="text.secondary" sx={{ mt: 1 }}>
								{t('academyReworkings.myBookingsDescription')}
							</Typography>
						</CardActionArea>
					</Paper>
				</Box>
			</Box>
		</Box>
	);
}
